import { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector } from 'react-redux';
import { Modal } from '@getsynapse/design-system';
import importIcon from 'assets/icons/import.svg';
import {
  attachTaskBundleToProject,
  fetchTaskBundles,
  previewTaskBundleDates,
} from 'state/TasksBundle/tasksBundleSlice';
import { Option } from 'utils/customTypes';
import intl from 'react-intl-universal';
import { useParams } from 'react-router-dom';
import { showNotificationBanner } from 'state/InlineNotification/inlineNotificationSlice';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import { SETTINGS_FILTERS } from 'utils/constants';
import {
  fetchTeamTasks,
  fetchUserTasks,
} from 'state/ProjectTasks/projectTaskSlice';
import {
  fetchProject,
  getProjectDataAttribute,
} from 'state/Project/projectSlice';
import { useAppThunkDispatch } from 'state/store';
import { parseDateStr } from 'state/TasksBundle/helpers';
import ImportStep from './ImportStep';
import PreviewStep from './PreviewStep/PreviewStep';

interface ImportTasksModalProps {
  showModal: boolean;
  onClose: () => void;
  onImportCallback?: () => void;
}

const toYYYYMMDD = (dateStr: string): string => {
  const { year, month, day } = parseDateStr(dateStr);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
    2,
    '0'
  )}`;
};

const ImportTasksModal: React.FC<ImportTasksModalProps> = ({
  showModal,
  onClose,
  onImportCallback,
}) => {
  const dispatch = useAppThunkDispatch();
  const { newTaskTypes = false, relativeDates = false } = useFlags();
  const projectStartDate = useSelector(getProjectDataAttribute('startDate')) as
    | string
    | undefined;

  const [selectedTaskBundle, setSelectedTaskBundle] = useState<Option>();
  const [step, setStep] = useState<'import' | 'preview'>('import');
  const [isLoading, setIsLoading] = useState(false);

  const { projectId } = useParams<{ projectId: string }>();

  const missingStartDate = newTaskTypes && relativeDates && !projectStartDate;
  const importDisabled = !selectedTaskBundle || missingStartDate;

  const onCloseModal = () => {
    setSelectedTaskBundle(undefined);
    setStep('import');
    onClose();
  };

  useEffect(() => {
    if (showModal) {
      dispatch(fetchTaskBundles(newTaskTypes));
    }
  }, [dispatch, showModal, newTaskTypes]);

  const attachBundle = async (applyDates?: boolean) => {
    try {
      await dispatch(
        attachTaskBundleToProject({
          projectId,
          taskBundleId: selectedTaskBundle?.value,
          useNewApi: newTaskTypes,
          applyDates,
        })
      ).unwrap();
    } catch {
      dispatch(
        showNotificationBanner({
          notificationVariant: 'error',
          notificationText: intl.get('ERROR_SNACKBAR_MESSAGE'),
        })
      );
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    if (onImportCallback) {
      onImportCallback();
    }
    onCloseModal();
    if (!onImportCallback) {
      await Promise.all([
        dispatch(
          showNotificationBanner({
            notificationVariant: 'success',
            notificationText: intl.get(
              'TASKS.IMPORT_TASKS_MODAL.SUCCESS_MESSAGE'
            ),
          })
        ),
        dispatch(fetchProject(projectId)),
        dispatch(fetchUserTasks(projectId)),
        dispatch(fetchTeamTasks(projectId)),
        dispatch(
          fetchFilterSettingByType(SETTINGS_FILTERS.PROJECT_TEAM_TASKS_TABLE)
        ),
        dispatch(
          fetchFilterSettingByType(SETTINGS_FILTERS.MY_PROJECT_TASKS_TABLE)
        ),
      ]);
    }
  };

  const onImportTasksFromBundle = async () => {
    if (!selectedTaskBundle?.value) return;
    setIsLoading(true);

    if (newTaskTypes && relativeDates && projectStartDate) {
      try {
        const result = await dispatch(
          previewTaskBundleDates({
            taskBundleId: selectedTaskBundle.value,
            projectStartDate: toYYYYMMDD(projectStartDate),
          })
        ).unwrap();

        if (result.hasScheduledTasks) {
          setStep('preview');
          setIsLoading(false);
          return;
        }
      } catch {
        dispatch(
          showNotificationBanner({
            notificationVariant: 'error',
            notificationText: intl.get('ERROR_SNACKBAR_MESSAGE'),
          })
        );
        setIsLoading(false);
        return;
      }
    }

    await attachBundle();
  };

  const onConfirmFromPreview = async () => {
    setIsLoading(true);
    await attachBundle(true);
  };

  const modalTitle =
    step === 'preview'
      ? intl.get('TASKS.IMPORT_TASKS_MODAL.PREVIEW.APPLY_BUNDLE_TITLE', {
          bundleName: selectedTaskBundle?.label ?? '',
        })
      : intl.get('TASKS.IMPORT_TASKS_MODAL.TITLE');

  type ModalActionButtons = NonNullable<
    React.ComponentProps<typeof Modal>['actionButtons']
  >;

  const actionButtons: ModalActionButtons =
    step === 'import'
      ? [
          {
            children: intl.get('IMPORT'),
            loading: isLoading,
            disabled: importDisabled,
            onClick: importDisabled ? undefined : onImportTasksFromBundle,
            'data-cy': 'import-tasks-modal-import_button',
            ...(missingStartDate
              ? {
                  buttonTooltip: {
                    children: intl.get(
                      'TASKS.IMPORT_TASKS_MODAL.PREVIEW.NO_START_DATE_TOOLTIP'
                    ),
                    openMode: 'hover1',
                    position: 'topLeft',
                    timeout: 0,
                    showPopper: true,
                    contentProps: {
                      className: 'rounded text-sm w-60 whitespace-normal z-50',
                    },
                  },
                }
              : {}),
          },
          {
            children: intl.get('CANCEL'),
            variant: 'tertiary',
            disabled: isLoading,
            onClick: onCloseModal,
          },
        ]
      : [
          {
            children: intl.get(
              'TASKS.IMPORT_TASKS_MODAL.PREVIEW.ADD_TASKS_BUTTON'
            ),
            loading: isLoading,
            onClick: onConfirmFromPreview,
            'data-cy': 'preview-add-tasks-button',
          },
          {
            children: intl.get(
              'TASKS.IMPORT_TASKS_MODAL.PREVIEW.SELECT_ANOTHER_BUNDLE'
            ),
            variant: 'secondary',
            disabled: isLoading,
            onClick: () => {
              setSelectedTaskBundle(undefined);
              setStep('import');
            },
            'data-cy': 'preview-select-another-bundle-button',
          },
          {
            children: intl.get('CANCEL'),
            variant: 'tertiary',
            disabled: isLoading,
            onClick: onCloseModal,
            'data-cy': 'preview-cancel-button',
          },
        ];

  return (
    <Modal
      title={modalTitle}
      titleIcon={step === 'import' ? { src: importIcon } : undefined}
      isOpen={showModal}
      closeModal={onCloseModal}
      aria-label={modalTitle}
      data-cy='import-tasks-modal'
      size={step === 'import' ? 'medium' : 'large'}
      childrenClassName='max-h-120'
      overlayClassName='z-50'
      actionButtons={actionButtons}
    >
      {step === 'import' ? (
        <ImportStep onSelectTaskBundle={setSelectedTaskBundle} />
      ) : (
        <PreviewStep />
      )}
    </Modal>
  );
};

export default ImportTasksModal;
